import React, { useState, useEffect } from 'react';
import { Plane, Plus, Calendar, CheckCircle, XCircle, CalendarCheck, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import CalendarioAnualFerias from './CalendarioAnualFerias';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const PedidosFeriasTab = () => {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNovoPedidoDialog, setShowNovoPedidoDialog] = useState(false);
  const [diasSelecionados, setDiasSelecionados] = useState([]);
  
  // Estado para férias confirmadas/definitivas
  const [feriasConfirmadas, setFeriasConfirmadas] = useState(null);
  const [loadingFerias, setLoadingFerias] = useState(false);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  
  const [novoPedido, setNovoPedido] = useState({
    data_inicio: '',
    data_fim: '',
    dias_selecionados: [],
    observacao: ''
  });

  // Helper para formatar data de forma segura
  const formatarDataSegura = (dataStr) => {
    if (!dataStr) return 'Data indisponível';
    
    try {
      const data = new Date(dataStr);
      if (isNaN(data.getTime())) {
        return 'Data inválida';
      }
      return data.toLocaleDateString('pt-PT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', dataStr, error);
      return 'Data inválida';
    }
  };

  const formatarDataHoraSegura = (dataStr) => {
    if (!dataStr) return null;
    
    try {
      const data = new Date(dataStr);
      if (isNaN(data.getTime())) {
        return null;
      }
      return {
        data: data.toLocaleDateString('pt-PT', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        hora: data.toLocaleTimeString('pt-PT', {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
    } catch (error) {
      console.error('Erro ao formatar data/hora:', dataStr, error);
      return null;
    }
  };

  useEffect(() => {
    carregarPedidos();
    carregarFeriasConfirmadas();
  }, []);
  
  // Recarregar férias quando o ano muda
  useEffect(() => {
    carregarFeriasConfirmadas();
  }, [anoSelecionado]);
  
  // Carregar férias confirmadas (mapa definitivo)
  const carregarFeriasConfirmadas = async () => {
    try {
      setLoadingFerias(true);
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user'));
      
      const response = await axios.get(`${API_URL}/api/ferias-definitivas/mapa/${anoSelecionado}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrar apenas as férias do colaborador atual (verificar id E uuid)
      const minhasFerias = response.data.colaboradores?.find(
        c => c.colaborador_id === user.id || c.colaborador_id === user.uuid
      );
      
      setFeriasConfirmadas({
        status: response.data.status,
        mensagem: response.data.mensagem,
        confirmado_em: response.data.confirmado_em,
        confirmado_por: response.data.confirmado_por,
        dias: minhasFerias?.dias || [],
        observacoes: minhasFerias?.observacoes || ''
      });
    } catch (error) {
      console.error('Erro ao carregar férias confirmadas:', error);
      setFeriasConfirmadas(null);
    } finally {
      setLoadingFerias(false);
    }
  };

  const carregarPedidos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/api/menu-colaborador/pedidos-ferias/meus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // DEBUG: Ver o que está a chegar do backend
      console.log('📅 Pedidos de férias recebidos:', JSON.stringify(response.data, null, 2));
      
      setPedidos(response.data);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
      const msg = error.response?.data?.detail || error.message || 'Erro desconhecido';
      toast.error(`Erro ao carregar pedidos: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  // Função para agrupar dias em períodos consecutivos
  const agruparDiasEmPeriodos = (dias) => {
    if (!dias || dias.length === 0) return [];
    
    const diasOrdenados = [...dias].sort();
    const periodos = [];
    let periodoAtual = { inicio: diasOrdenados[0], fim: diasOrdenados[0] };
    
    for (let i = 1; i < diasOrdenados.length; i++) {
      const diaAnterior = new Date(diasOrdenados[i - 1] + 'T00:00:00');
      const diaAtual = new Date(diasOrdenados[i] + 'T00:00:00');
      
      // Verificar se é consecutivo (diferença de 1 dia)
      const diffDias = Math.round((diaAtual - diaAnterior) / (1000 * 60 * 60 * 24));
      
      if (diffDias === 1) {
        // Dia consecutivo - estender período atual
        periodoAtual.fim = diasOrdenados[i];
      } else {
        // Dia não consecutivo - salvar período atual e começar novo
        periodos.push({ ...periodoAtual });
        periodoAtual = { inicio: diasOrdenados[i], fim: diasOrdenados[i] };
      }
    }
    
    // Adicionar último período
    periodos.push(periodoAtual);
    
    return periodos;
  };

  const handleCriarPedido = async () => {
    try {
      if (diasSelecionados.length === 0) {
        toast.error('Selecione pelo menos um dia de férias');
        return;
      }

      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user'));
      
      // Agrupar dias em períodos consecutivos
      const periodos = agruparDiasEmPeriodos(diasSelecionados);
      const diasOrdenados = [...diasSelecionados].sort();
      
      // Criar UM ÚNICO pedido com todos os períodos
      const pedidoData = {
        colaborador_id: user.id,
        colaborador_nome: user.nome,
        data_inicio: diasOrdenados[0],  // Primeiro dia de todos
        data_fim: diasOrdenados[diasOrdenados.length - 1],  // Último dia de todos
        dias_selecionados: diasSelecionados,
        periodos: periodos,  // Array com os períodos distintos
        total_periodos: periodos.length,
        observacao: novoPedido.observacao
      };
      
      await axios.post(
        `${API_URL}/api/menu-colaborador/pedidos-ferias`,
        pedidoData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const msg = periodos.length > 1 
        ? `Pedido criado com ${periodos.length} períodos de férias!`
        : 'Pedido de férias criado com sucesso!';
      toast.success(msg);
      setShowNovoPedidoDialog(false);
      setDiasSelecionados([]);
      setNovoPedido({
        data_inicio: '',
        data_fim: '',
        dias_selecionados: [],
        observacao: ''
      });
      carregarPedidos();
    } catch (error) {
      console.error('Erro ao criar pedido:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar pedido');
    }
  };

  // Função para calcular a Páscoa (algoritmo de Meeus/Jones/Butcher)
  const calcularPascoa = (ano) => {
    const a = ano % 19;
    const b = Math.floor(ano / 100);
    const c = ano % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const mes = Math.floor((h + l - 7 * m + 114) / 31);
    const dia = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(ano, mes - 1, dia);
  };

  // Função para obter feriados de um ano
  const getFeriados = (ano) => {
    const feriados = [];
    
    // Feriados fixos
    feriados.push(`${ano}-01-01`); // Ano Novo
    feriados.push(`${ano}-04-25`); // 25 de Abril
    feriados.push(`${ano}-05-01`); // Dia do Trabalhador
    feriados.push(`${ano}-06-10`); // Dia de Portugal
    feriados.push(`${ano}-08-15`); // Assunção de Nossa Senhora
    feriados.push(`${ano}-08-20`); // Feriado Municipal (20 de Agosto)
    feriados.push(`${ano}-10-05`); // Implantação da República
    feriados.push(`${ano}-11-01`); // Todos os Santos
    feriados.push(`${ano}-12-01`); // Restauração da Independência
    feriados.push(`${ano}-12-08`); // Imaculada Conceição
    feriados.push(`${ano}-12-25`); // Natal

    // Feriados móveis baseados na Páscoa
    const pascoa = calcularPascoa(ano);
    const adicionarDias = (data, dias) => {
      const novaData = new Date(data);
      novaData.setDate(novaData.getDate() + dias);
      return novaData.toISOString().split('T')[0];
    };
    
    feriados.push(pascoa.toISOString().split('T')[0]); // Páscoa
    feriados.push(adicionarDias(pascoa, -47)); // Carnaval (Terça-feira)
    feriados.push(adicionarDias(pascoa, -2)); // Sexta-feira Santa
    feriados.push(adicionarDias(pascoa, 60)); // Corpo de Deus

    return feriados;
  };

  // Verificar se é fim de semana
  const isFimDeSemana = (data) => {
    const diaSemana = data.getDay();
    return diaSemana === 0 || diaSemana === 6; // 0 = Domingo, 6 = Sábado
  };

  // Verificar se é feriado
  const isFeriado = (data) => {
    const ano = data.getFullYear();
    const dataStr = data.toISOString().split('T')[0];
    const feriados = getFeriados(ano);
    return feriados.includes(dataStr);
  };

  // Calcular dias ÚTEIS (excluindo fins de semana e feriados)
  const calcularDias = (dataInicio, dataFim) => {
    if (!dataInicio || !dataFim) return 0;
    
    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);
    let diasUteis = 0;
    
    const dataAtual = new Date(inicio);
    while (dataAtual <= fim) {
      // Só conta se NÃO for fim de semana e NÃO for feriado
      if (!isFimDeSemana(dataAtual) && !isFeriado(dataAtual)) {
        diasUteis++;
      }
      dataAtual.setDate(dataAtual.getDate() + 1);
    }
    
    return diasUteis;
  };

  // Calcular dias úteis de um pedido (considera periodos se existirem)
  const calcularDiasPedido = (pedido) => {
    // Se tem dias_selecionados, filtrar apenas dias úteis (excluir fins de semana e feriados)
    if (pedido.dias_selecionados && pedido.dias_selecionados.length > 0) {
      const diasUteis = pedido.dias_selecionados.filter(diaStr => {
        const data = new Date(diaStr + 'T00:00:00');
        const diaSemana = data.getDay();
        const eFimDeSemana = diaSemana === 0 || diaSemana === 6;
        
        // Verificar feriados (Portugal)
        const ano = data.getFullYear();
        const feriadosFixos = [
          `${ano}-01-01`, `${ano}-04-25`, `${ano}-05-01`, `${ano}-06-10`,
          `${ano}-08-15`, `${ano}-08-20`, `${ano}-10-05`, `${ano}-11-01`,
          `${ano}-12-01`, `${ano}-12-08`, `${ano}-12-25`
        ];
        const eFeriado = feriadosFixos.includes(diaStr);
        
        return !eFimDeSemana && !eFeriado;
      });
      return diasUteis.length;
    }
    
    // Se tem periodos, calcular dias úteis de cada período
    if (pedido.periodos && pedido.periodos.length > 0) {
      let total = 0;
      pedido.periodos.forEach(periodo => {
        total += calcularDias(periodo.inicio, periodo.fim);
      });
      return total;
    }
    
    // Fallback: calcular entre data_inicio e data_fim
    return calcularDias(pedido.data_inicio, pedido.data_fim);
  };

  const getEstadoBadge = (estado) => {
    const cores = {
      'Pendente': 'bg-yellow-100 text-yellow-800',
      'Aprovado': 'bg-green-100 text-green-800',
      'Rejeitado': 'bg-red-100 text-red-800'
    };
    
    return (
      <Badge className={cores[estado] || 'bg-gray-100 text-gray-800'}>
        {estado}
      </Badge>
    );
  };

  // Nomes dos meses em português
  const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  // Componente Calendário Mini - Mostra todos os meses com dias selecionados
  const CalendarioMiniColaborador = ({ pedido }) => {
    // Obter dias do pedido
    const diasPedido = pedido.dias_selecionados || [];
    
    // Se não tem dias_selecionados, gerar a partir das datas
    const diasFinais = diasPedido.length > 0 ? diasPedido : (() => {
      const dias = [];
      if (pedido.data_inicio && pedido.data_fim) {
        let current = new Date(pedido.data_inicio);
        const end = new Date(pedido.data_fim);
        while (current <= end) {
          dias.push(current.toISOString().split('T')[0]);
          current.setDate(current.getDate() + 1);
        }
      }
      return dias;
    })();
    
    // Agrupar dias por mês/ano
    const mesesComDias = {};
    diasFinais.forEach(diaStr => {
      const data = new Date(diaStr + 'T00:00:00');
      const chave = `${data.getFullYear()}-${data.getMonth()}`;
      if (!mesesComDias[chave]) {
        mesesComDias[chave] = {
          ano: data.getFullYear(),
          mes: data.getMonth(),
          dias: []
        };
      }
      mesesComDias[chave].dias.push(diaStr);
    });
    
    const mesesOrdenados = Object.values(mesesComDias).sort((a, b) => {
      if (a.ano !== b.ano) return a.ano - b.ano;
      return a.mes - b.mes;
    });
    
    if (mesesOrdenados.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-2 mt-3">
        {mesesOrdenados.map(({ ano, mes, dias }) => {
          const getDiasNoMes = () => new Date(ano, mes + 1, 0).getDate();
          const getPrimeiroDiaSemana = () => new Date(ano, mes, 1).getDay();
          
          return (
            <div key={`${ano}-${mes}`} className="bg-gray-50 rounded-lg border p-2 w-36">
              <div className="text-center font-semibold text-xs mb-1">{meses[mes]} {ano}</div>
              <div className="grid grid-cols-7 gap-0.5 text-center" style={{ fontSize: '9px' }}>
                {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
                  <div key={i} className="font-medium text-gray-400">{d}</div>
                ))}
                {Array.from({ length: getPrimeiroDiaSemana() }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                {Array.from({ length: getDiasNoMes() }, (_, i) => {
                  const dia = i + 1;
                  const dataStr = `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                  const isSelecionado = dias.includes(dataStr);
                  
                  return (
                    <div 
                      key={dia}
                      className={`py-0.5 rounded ${
                        isSelecionado ? 'bg-blue-500 text-white font-bold' : ''
                      }`}
                    >
                      {dia}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">A carregar...</div>;
  }

  // Componente para mostrar calendário mini das férias confirmadas
  const CalendarioFeriasConfirmadas = ({ dias, ano }) => {
    if (!dias || dias.length === 0) return null;
    
    // Agrupar dias por mês
    const mesesComDias = {};
    dias.forEach(diaStr => {
      const data = new Date(diaStr + 'T00:00:00');
      const chave = `${data.getFullYear()}-${data.getMonth()}`;
      if (!mesesComDias[chave]) {
        mesesComDias[chave] = {
          ano: data.getFullYear(),
          mes: data.getMonth(),
          dias: []
        };
      }
      mesesComDias[chave].dias.push(diaStr);
    });
    
    const mesesOrdenados = Object.values(mesesComDias).sort((a, b) => {
      if (a.ano !== b.ano) return a.ano - b.ano;
      return a.mes - b.mes;
    });
    
    return (
      <div className="flex flex-wrap gap-3 mt-3">
        {mesesOrdenados.map(({ ano: anoMes, mes, dias: diasMes }) => {
          const getDiasNoMes = () => new Date(anoMes, mes + 1, 0).getDate();
          const getPrimeiroDiaSemana = () => new Date(anoMes, mes, 1).getDay();
          
          return (
            <div key={`${anoMes}-${mes}`} className="bg-green-50 rounded-lg border border-green-200 p-2 w-40">
              <div className="text-center font-semibold text-xs mb-1 text-green-800">{meses[mes]} {anoMes}</div>
              <div className="grid grid-cols-7 gap-0.5 text-center" style={{ fontSize: '9px' }}>
                {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
                  <div key={i} className="font-medium text-gray-400">{d}</div>
                ))}
                {Array.from({ length: getPrimeiroDiaSemana() }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                {Array.from({ length: getDiasNoMes() }, (_, i) => {
                  const dia = i + 1;
                  const dataStr = `${anoMes}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                  const isSelecionado = diasMes.includes(dataStr);
                  
                  return (
                    <div 
                      key={dia}
                      className={`py-0.5 rounded ${
                        isSelecionado ? 'bg-green-500 text-white font-bold' : ''
                      }`}
                    >
                      {dia}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* SECÇÃO: Férias Confirmadas/Definitivas */}
      <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-green-600" />
              <CardTitle className="text-lg">Minhas Férias Aprovadas</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setAnoSelecionado(a => a - 1)}
                className="h-7 w-7 p-0"
              >
                ‹
              </Button>
              <span className="font-bold text-lg w-12 text-center">{anoSelecionado}</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setAnoSelecionado(a => a + 1)}
                className="h-7 w-7 p-0"
              >
                ›
              </Button>
            </div>
          </div>
          <CardDescription>Férias confirmadas pelo administrador para {anoSelecionado}</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingFerias ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              <span className="ml-2 text-gray-500">A carregar...</span>
            </div>
          ) : feriasConfirmadas?.status === 'confirmado' && feriasConfirmadas.dias?.length > 0 ? (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-green-100 text-green-800 border-green-300">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Confirmado
                </Badge>
                <span className="text-sm text-green-700 font-medium">
                  {feriasConfirmadas.dias.filter(d => {
                    const data = new Date(d + 'T00:00:00');
                    return !isFimDeSemana(data) && !isFeriado(data);
                  }).length} dias úteis de férias
                </span>
              </div>
              
              {/* Calendário visual */}
              <CalendarioFeriasConfirmadas dias={feriasConfirmadas.dias} ano={anoSelecionado} />
              
              {feriasConfirmadas.observacoes && (
                <div className="mt-3 p-2 bg-white/50 rounded-lg text-sm text-gray-600">
                  <span className="font-medium">Observações:</span> {feriasConfirmadas.observacoes}
                </div>
              )}
              
              {feriasConfirmadas.confirmado_em && (
                <p className="text-xs text-gray-500 mt-2">
                  Confirmado em {new Date(feriasConfirmadas.confirmado_em).toLocaleDateString('pt-PT')}
                  {feriasConfirmadas.confirmado_por && ` por ${feriasConfirmadas.confirmado_por}`}
                </p>
              )}
            </div>
          ) : feriasConfirmadas?.mensagem ? (
            <div className="flex items-center gap-2 py-4 text-yellow-700 bg-yellow-50 rounded-lg px-4">
              <Info className="h-5 w-5" />
              <p>{feriasConfirmadas.mensagem}</p>
            </div>
          ) : (
            <div className="flex items-center gap-2 py-4 text-gray-500">
              <Info className="h-5 w-5" />
              <p>Ainda não há férias confirmadas para {anoSelecionado}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SECÇÃO: Pedidos de Férias */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Pedidos de Férias</h2>
          <p className="text-sm text-gray-600 mt-1">Gerir pedidos de férias</p>
        </div>
        
        <Dialog open={showNovoPedidoDialog} onOpenChange={setShowNovoPedidoDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Novo Pedido
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] lg:max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Novo Pedido de Férias</DialogTitle>
              <DialogDescription>
                Preencha o período de férias pretendido
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Calendário Anual */}
              <div className="space-y-2">
                <Label>Selecione os dias de férias no calendário *</Label>
                <CalendarioAnualFerias 
                  diasSelecionados={diasSelecionados}
                  onDiasChange={setDiasSelecionados}
                />
              </div>
              
              {diasSelecionados.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <strong>Total de dias úteis:</strong> {diasSelecionados.filter(data => {
                    const d = new Date(data);
                    return !isFimDeSemana(d) && !isFeriado(d);
                  }).length} dias
                  <span className="text-gray-500 ml-2">(excluindo fins de semana e feriados)</span>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Observação (opcional)</Label>
                <Textarea
                  value={novoPedido.observacao}
                  onChange={(e) => setNovoPedido({...novoPedido, observacao: e.target.value})}
                  placeholder="Alguma informação adicional..."
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowNovoPedidoDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCriarPedido}>
                Criar Pedido
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Plane className="h-5 w-5 mr-2" />
            Meus Pedidos de Férias
          </CardTitle>
          <CardDescription>Histórico dos pedidos de férias</CardDescription>
        </CardHeader>
        <CardContent>
          {pedidos.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Ainda não criou nenhum pedido de férias</p>
          ) : (
            <div className="space-y-4">
              {pedidos.map(pedido => {
                // Normalizar campos (backend pode enviar "status" ou "estado", "data_criacao" ou "created_at")
                const dataCriacao = pedido.created_at || pedido.data_criacao;
                const observacao = pedido.observacao || pedido.motivo;
                const estado = pedido.estado || pedido.status || 'Pendente';
                const totalDias = calcularDiasPedido(pedido);
                
                return (
                <Card key={pedido.id}>
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={
                            estado === 'Aprovado' ? 'bg-green-100 text-green-800' :
                            estado === 'Rejeitado' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {estado}
                          </Badge>
                          <span className="text-sm text-blue-600 font-semibold">
                            {totalDias} dias úteis
                          </span>
                          {pedido.total_periodos > 1 && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">
                              {pedido.total_periodos} períodos
                            </Badge>
                          )}
                        </div>
                        
                        {/* Mostrar períodos distintos se existirem */}
                        {pedido.periodos && pedido.periodos.length > 1 ? (
                          <div className="space-y-1">
                            <p className="text-xs text-gray-500 font-medium">Períodos solicitados:</p>
                            {pedido.periodos.map((periodo, idx) => (
                              <p key={idx} className="font-semibold text-base pl-2 border-l-2 border-purple-300">
                                {formatarDataSegura(periodo.inicio)} - {formatarDataSegura(periodo.fim)}
                              </p>
                            ))}
                          </div>
                        ) : (
                          <h4 className="font-semibold text-lg">
                            {formatarDataSegura(pedido.data_inicio)} - {formatarDataSegura(pedido.data_fim)}
                          </h4>
                        )}
                        
                        {(() => {
                          const dataHora = formatarDataHoraSegura(dataCriacao);
                          return dataHora ? (
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Criado em {dataHora.data} às {dataHora.hora}
                            </p>
                          ) : null;
                        })()}
                        
                        {/* Calendário visual com todos os meses */}
                        <CalendarioMiniColaborador pedido={pedido} />
                      </div>
                    </div>
                    
                    {observacao && (
                      <div className="mb-3 p-3 bg-gray-50 rounded-md">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Observação:</p>
                        <p className="text-sm text-gray-700">{observacao}</p>
                      </div>
                    )}
                    
                    {pedido.observacao_admin && (
                      <div className="p-3 bg-blue-50 rounded-md">
                        <p className="text-xs font-semibold text-blue-900 mb-1">Decisão da Gestão:</p>
                        <p className="text-sm text-blue-900">{pedido.observacao_admin}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Por {pedido.admin_nome} em {formatarDataSegura(pedido.data_decisao)}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PedidosFeriasTab;